import React from 'react';
import { ColumnDetail } from 'helpers/createUseTable';
import { Cell, CellOptions } from './Cell';
import { makeRow, RowConfig, Checkbox, SingleCheckbox } from './FlexTable';
import { useDetectOverflow } from 'helpers/useDetectOverflow';
import { toCamelCase } from 'helpers/formatFormFieldNames';
import i18n from 'i18n';

type MiddleTableProps<T = any> = {
    idColumn: string;
    columns: ColumnDetail<any>[];
    columnData: any[];
    rowHovered: number | null;
    selectedIds?: number[];
    handleMouseEnterRow(e: any, i: number): void;
    onRowClick?(id: number): void;
    checkbox?: Checkbox;
    rowButton?(data: any): React.ReactNode;
    hiddenColumns?: string[];
    formatPrimitives(value: unknown): any;
    rowFormatter?(r: T): { [K in keyof T]: React.ReactNode };
    noRowHover?: boolean;
    rowCellOptions?: { [K in keyof T]?: CellOptions };
    toggleColumnSort?: (k: string) => void;
    sortBy: { [k: string]: 'ASC' | 'DESC' };
    singleCheckbox?: SingleCheckbox;
};

const MiddleTable: React.FC<MiddleTableProps> = ({
    idColumn,
    columns,
    columnData,
    rowHovered,
    selectedIds = [],
    handleMouseEnterRow,
    checkbox,
    onRowClick,
    rowButton,
    hiddenColumns,
    formatPrimitives,
    rowFormatter,
    noRowHover,
    rowCellOptions = {},
    toggleColumnSort,
    sortBy,
    singleCheckbox,
}) => {
    const { ref, hasOverflow } = useDetectOverflow([checkbox?.selectedIds]);

    const getColumnLabel = (c: ColumnDetail): { label: string; found: boolean } => {
        // Convert labelKey or labelValue to camelCase and check common namespaces
        const camelKey = (c.labelKey || c.labelValue || '')
            .replace(/[\s/]+(.)/g, (_, ch: string) => ch.toUpperCase())
            .replace(/^\w/, (ch) => ch.toLowerCase());
        const namespaces = ['transactions', 'moveMoney', 'businessUsers', 'crypto', 'approvals', 'common'];
        for (const ns of namespaces) {
            if (i18n.exists(`${ns}:${camelKey}`)) return { label: i18n.t(`${ns}:${camelKey}`) as string, found: true };
        }
        return { label: c.labelValue, found: false };
    };

    const visibleColumns = columns.filter(
        (c) => !hiddenColumns?.includes(c.columnKey.toString().toLowerCase())
    );
    const colCount = visibleColumns.length + (rowButton ? 1 : 0);

    return (
        <div
            id="MiddleTable_OverflowEnabled"
            ref={ref}
            className={hasOverflow ? 'MiddleTable BoxShadow' : 'MiddleTable'}
            style={{ '--col-count': colCount } as React.CSSProperties}
        >
            <div className="Head">
                {columns
                    .filter((c) => !hiddenColumns?.includes(c.columnKey.toString().toLowerCase()))
                    .map((c, i) => {
                        const sortByValue = sortBy[String(c.columnKey)] ?? 'NONE';
                        const isSorting = sortByValue === 'ASC' || sortByValue === 'DESC';
                        const { bSortable } = c;

                        return (
                            <Cell
                                key={i}
                                options={rowCellOptions[toCamelCase(c.columnKey as string)]}
                                sortable={bSortable}
                                onClick={() =>
                                    bSortable &&
                                    toggleColumnSort &&
                                    toggleColumnSort(String(c.columnKey))
                                }
                            >
                                {(() => {
                                    const { label, found } = getColumnLabel(c);
                                    return <span className={`${bSortable && isSorting ? 'Sorting' : ''}${found ? ' notranslate' : ''}`} {...(found ? { translate: 'no' as any } : {})}>
                                        {label}
                                    </span>;
                                })()}
                                {bSortable && (
                                    <i
                                        className={
                                            sortByValue === 'ASC'
                                                ? 'SortIcon ASC'
                                                : sortByValue === 'DESC'
                                                ? 'SortIcon DESC'
                                                : 'SortIcon'
                                        }
                                    />
                                )}
                            </Cell>
                        );
                    })}
                {rowButton && (
                    <Cell
                        options={{
                            cellType: 'buttonCell',
                            noSortIcon: true,
                            ...rowCellOptions?.['_button'],
                        }}
                    />
                )}
            </div>
            <div className="TableBody">
                {columnData.map((d, i) =>
                    makeRow({
                        id: d[idColumn].toString(),
                        r: d,
                        index: i,
                        columns,
                        formatPrimitives,
                        hiddenColumns: hiddenColumns ?? [],
                        rowHovered,
                        handleMouseEnterRow,
                        onRowClick: onRowClick ? () => onRowClick(d[idColumn]) : null,
                        rowButton: rowButton ? () => rowButton(d) : undefined,
                        formatRow: rowFormatter,
                        checkbox,
                        noRowHover,
                        rowSelected:
                            checkbox?.selectedIds.includes(d[idColumn]) ||
                            checkbox?.selectedIds.includes(d[idColumn].toString()) ||
                            checkbox?.selectedIds.includes(parseInt(d[idColumn], 10)) ||
                            selectedIds.includes(d[idColumn]) ||
                            selectedIds.includes(d[idColumn].toString()) ||
                            selectedIds.includes(parseInt(d[idColumn], 10)),
                        hideRowOptionComponent: checkbox ? checkbox.selectedIds.length > 0 : false,
                        rowCellOptions,
                        singleCheckbox,
                    })
                )}
            </div>
        </div>
    );
};

export { MiddleTable };
